// #20 CheckTile and RadioTile: the 40 px tile of an attestation or agreement line (.mc-tile) on --card-2, with its box
// (a check for a checkbox, a dot for a radio), the words and an optional second line and right slot. A CheckTile is a
// <button role="checkbox" aria-checked>; RadioTile is a radiogroup of tiles with one tab stop (useRoving: arrows with
// wrap, Home/End, selection follows focus). `plain` drops the surface (a tile inside a card row). DC-24: can={false}
// renders nothing; `blocked` is the dashed .mc-dis with aria-disabled and the inline .mc-sr reason (mirrored by the
// tooltip) and the choice does not change; mode="view" draws the chosen words, no control.

import { useId, type ReactNode } from "react";
import { useBlocked, type Blocked, type ControlProps } from "../core";
import { Icon } from "../core";
import { ACCESS_LABELS } from "../labels";
import { cx } from "../lib/cx";
import { useHs, type Hs } from "../lib/hs";
import { Tooltip } from "../lib/Tooltip";
import { useRoving } from "../lib/useRoving";

export interface CheckTileProps extends ControlProps {
  /** The tile's words (a label). Absent: the tile is named by `labelledBy` (a Kanban card's selection). */
  children?: ReactNode;
  /** A second line under the words (12 px, --text-2). */
  sub?: ReactNode | undefined;
  checked: boolean;
  onChange?: ((checked: boolean) => void) | undefined;
  /** No surface: the box and the words only. */
  plain?: boolean | undefined;
  /** Right-aligned content (a pill, a date). */
  right?: ReactNode | undefined;
  /** The id of the element that names the tile when it carries no words of its own. */
  labelledBy?: string | undefined;
  id?: string | undefined;
  className?: string | undefined;
}

export function CheckTile({ children, sub, checked, onChange, plain, right, labelledBy, id, can, blocked, mode, hs, className }: CheckTileProps) {
  const generated = useId();
  const wordsId = `${generated}-t`;
  const subId = `${generated}-s`;
  const hot = useHs(hs);
  const block = useBlocked(blocked, sub ? subId : undefined);
  if (can === false) return null;
  const hasWords = children !== undefined && children !== null && children !== false;
  if (mode === "view") {
    return (
      <span className={cx("mc-tile", "mc-tile-view", plain && "mc-tile-plain", className)}>
        <span className="mc-tile-tx"><span className="mc-tile-w">{children}</span>{sub ? <span>{sub}</span> : null}</span>
        <b className="mc-tile-r">{checked ? ACCESS_LABELS.Yes : ACCESS_LABELS.No}</b>
      </span>
    );
  }
  const tile = (
    <button
      type="button"
      role="checkbox"
      id={id}
      aria-checked={checked}
      aria-labelledby={hasWords ? wordsId : labelledBy}
      aria-describedby={block.describedBy}
      {...block.props}
      className={cx("mc-tile", checked && "mc-on", plain && "mc-tile-plain", !hasWords && "mc-tile-box", blocked && "mc-dis", className)}
      onClick={() => { if (!blocked) onChange?.(!checked); }}
      {...hot}
    >
      <span className="mc-tile-bx" aria-hidden="true">{checked ? <Icon name="check" size={14} /> : null}</span>
      {hasWords || sub ? (
        <span className="mc-tile-tx">
          {hasWords ? <span className="mc-tile-w" id={wordsId}>{children}</span> : null}
          {sub ? <span id={subId}>{sub}</span> : null}
        </span>
      ) : null}
      {right ? <span className="mc-tile-r">{right}</span> : null}
    </button>
  );
  return (
    <>
      {blocked ? <Tooltip label={blocked.reason} mode="mirror">{tile}</Tooltip> : tile}
      {block.reason}
    </>
  );
}

export interface RadioTileOption {
  value: string;
  /** The option's words (a label). */
  label: ReactNode;
  sub?: ReactNode | undefined;
  right?: ReactNode | undefined;
  /** This option alone is rule-blocked: focusable, aria-disabled, its reason inline; choosing it does nothing. */
  blocked?: Blocked | undefined;
  /** false: the option is not drawn. */
  can?: boolean | undefined;
  hs?: Hs | undefined;
}

export interface RadioTileProps extends Omit<ControlProps, "hs"> {
  /** The group's name: visible words above the tiles (a label) ... */
  label?: ReactNode | undefined;
  /** ... or the id of an element that names it. */
  labelledBy?: string | undefined;
  options: readonly RadioTileOption[];
  /** The chosen value; "" or absent: nothing chosen yet. */
  value?: string | undefined;
  onChange?: ((value: string) => void) | undefined;
  plain?: boolean | undefined;
  /** Lay the tiles in a row (default a column). */
  row?: boolean | undefined;
  className?: string | undefined;
}

function RadioTileItem({ option, checked, groupBlocked, plain, item, choose, index }: {
  option: RadioTileOption;
  checked: boolean;
  groupBlocked: boolean;
  plain: boolean | undefined;
  item: ReturnType<ReturnType<typeof useRoving>["item"]>;
  choose: (index: number) => void;
  index: number;
}) {
  const generated = useId();
  const hot = useHs(option.hs);
  const subId = `${generated}-s`;
  const wordsId = `${generated}-t`;
  const block = useBlocked(option.blocked, option.sub ? subId : undefined);
  const tile = (
    <button
      type="button"
      role="radio"
      aria-checked={checked}
      aria-labelledby={wordsId}
      aria-describedby={block.describedBy}
      aria-disabled={option.blocked || groupBlocked ? true : undefined}
      className={cx("mc-tile", "mc-tile-radio", checked && "mc-on", plain && "mc-tile-plain", (option.blocked || groupBlocked) && "mc-dis")}
      {...item}
      onClick={() => choose(index)}
      {...hot}
    >
      <span className="mc-tile-rd" aria-hidden="true" />
      <span className="mc-tile-tx">
        <span className="mc-tile-w" id={wordsId}>{option.label}</span>
        {option.sub ? <span id={subId}>{option.sub}</span> : null}
      </span>
      {option.right ? <span className="mc-tile-r">{option.right}</span> : null}
    </button>
  );
  return (
    <>
      {option.blocked ? <Tooltip label={option.blocked.reason} mode="mirror">{tile}</Tooltip> : tile}
      {block.reason}
    </>
  );
}

export function RadioTile({ label, labelledBy, options, value, onChange, plain, row, can, blocked, mode, className }: RadioTileProps) {
  const generated = useId();
  const labelId = `${generated}-l`;
  const shown = options.filter((option) => option.can !== false);
  const active = shown.findIndex((option) => option.value === value);
  const block = useBlocked(blocked);
  const choose = (index: number) => {
    const option = shown[index];
    if (!option || blocked || option.blocked || option.value === value) return;
    onChange?.(option.value);
  };
  const roving = useRoving({ count: shown.length, active, selectOnFocus: true, onSelect: choose, wrap: true });
  if (can === false) return null;
  const heading = label !== undefined ? <span className="mc-tiles-l" id={labelId}>{label}</span> : null;
  if (mode === "view") {
    const chosen = shown[active];
    return (
      <div className={cx("mc-tiles", "mc-tiles-view", className)}>
        {heading}
        <span className="mc-tile-view-v">{chosen ? chosen.label : "—"}</span>
      </div>
    );
  }
  return (
    <div className={cx("mc-tiles", row && "mc-tiles-row", className)}>
      {heading}
      <div
        role="radiogroup"
        aria-labelledby={label !== undefined ? labelId : labelledBy}
        aria-disabled={blocked ? true : undefined}
        aria-describedby={block.describedBy}
        className="mc-tiles-g"
        {...roving.container}
      >
        {shown.map((option, index) => (
          <RadioTileItem
            key={option.value}
            option={option}
            index={index}
            checked={index === active}
            groupBlocked={Boolean(blocked)}
            plain={plain}
            item={roving.item(index)}
            choose={choose}
          />
        ))}
      </div>
      {block.reason}
    </div>
  );
}
